import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import cn from "classnames";
import styles from "./Settings.module.sass";
import ProfileInformation from "./ProfileInformation";
import TutorInformation from "./TutorInformation";
import CompanyInformation from "./AddressInformation";
import Confirmation from "./Confirmation";
import { getStudentByFolderId, updateFolder, getFolderById } from "../../services/folder.service";
import { updateStudent } from "../../services/student.service";

// Fonction utilitaire pour calculer l'âge
const calculateAge = (birthDate) => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};

const StudentCheckInformation = () => {
  const { id: folderId } = useParams(); // Récupérer l'ID du folder depuis l'URL

  // États pour la navigation et les données
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [studentData, setStudentData] = useState({});
  const [folderData, setFolderData] = useState({
    personalInfo: {},
    legalGuardianInfo: {},
    addressInfo: {}
  });

  // Configuration dynamique des étapes basée sur l'âge de l'étudiant
  const getSteps = () => {
    const studentAge = calculateAge(studentData?.birth_date);
    const isMinor = studentAge !== null && studentAge < 18;

    const baseSteps = [
      {
        id: 0,
        title: "Informations personnelles",
        component: ProfileInformation,
      }
    ];

    // Ajouter l'étape tuteur légal si l'étudiant est mineur
    if (isMinor) {
      baseSteps.push({
        id: 1,
        title: "Tuteur légal",
        component: TutorInformation,
      });
    }

    // Ajouter les étapes suivantes avec des IDs ajustés
    const nextId = isMinor ? 2 : 1;
    baseSteps.push(
      {
        id: nextId,
        title: "Adresse",
        component: CompanyInformation,
      },
      {
        id: nextId + 1,
        title: "Confirmation",
        component: Confirmation,
      }
    );

    return baseSteps;
  };

  const steps = getSteps();

  // Récupérer les informations de l'étudiant au chargement du composant
  useEffect(() => {
    const fetchStudentData = async () => {
      if (!folderId) {
        setError("ID du dossier manquant");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const student = await getStudentByFolderId(folderId);
        setStudentData(student);
        setError(null);
      } catch (error) {
        console.error('Erreur lors de la récupération des données de l\'étudiant:', error);
        setError("Impossible de récupérer les informations de l'étudiant");
      } finally {
        setLoading(false);
      }
    };

    fetchStudentData();
  }, [folderId]);

  // Navigation entre les étapes
  const goToNextStep = async () => {
    if (currentStep < steps.length - 1) {
      const studentAge = calculateAge(studentData?.birth_date);
      const isMinor = studentAge !== null && studentAge < 18;

      // Si on passe de l'étape 0 (ProfileInformation) à l'étape suivante
      if (currentStep === 0 && folderData.personalInfo && Object.keys(folderData.personalInfo).length > 0) {
        try {
          // Mettre à jour les informations personnelles de l'étudiant
          await updateStudent(studentData.id, folderData.personalInfo);
          console.log('Informations personnelles mises à jour');
        } catch (error) {
          console.error('Erreur lors de la mise à jour des informations personnelles:', error);
        }
      }

      // Si on passe de l'étape tuteur légal (étape 1 pour mineur) à l'étape suivante
      if (isMinor && currentStep === 1 && folderData.legalGuardianInfo && Object.keys(folderData.legalGuardianInfo).length > 0) {
        try {
          // Mettre à jour les informations du tuteur légal
          await updateStudent(studentData.id, folderData.legalGuardianInfo);
          console.log('Informations tuteur légal mises à jour');
        } catch (error) {
          console.error('Erreur lors de la mise à jour des informations tuteur légal:', error);
        }
      }

      // Si on passe de l'étape Adresse à la Confirmation
      const addressStepIndex = isMinor ? 2 : 1;
      if (currentStep === addressStepIndex) {
        try {
          // Mettre à jour les informations d'adresse de l'étudiant
          if (folderData.addressInfo && Object.keys(folderData.addressInfo).length > 0) {
            await updateStudent(studentData.id, folderData.addressInfo);
            console.log('Informations d\'adresse mises à jour');
          }

          // Mettre à jour la progression : marquer les infos étudiant comme remplies
          try {
            const currentFolder = await getFolderById(folderId);
            const currentProgression = currentFolder.progression || {
              "etudiant": {
                "infos_remplies": false,
                "valide": false
              },
              "entreprise": {
                "infos_remplies": false,
                "tuteur_assigne": false,
                "remuneration_ok": false,
                "opco_assigne": false,
                "valide": false
              },
              "ecole": {
                "cerfa_genere": false,
                "valide": false
              }
            };

            // Mettre à jour infos_remplies pour l'étudiant
            const updatedProgression = {
              ...currentProgression,
              etudiant: {
                ...currentProgression.etudiant,
                infos_remplies: true
              }
            };

            await updateFolder(folderId, {
              progression: updatedProgression
            });

            console.log('Progression mise à jour : infos_remplies = true');
          } catch (progressionError) {
            console.error('Erreur lors de la mise à jour de la progression:', progressionError);
          }
        } catch (error) {
          console.error('Erreur lors de la sauvegarde des données étudiant:', error);
        }
      }

      setCurrentStep(currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStep(stepIndex);
    }
  };

  // Mise à jour des données du formulaire
  const updateStepData = (stepKey, data) => {
    setFolderData(prev => ({
      ...prev,
      [stepKey]: typeof data === 'object' && data !== null ? { ...prev[stepKey], ...data } : data
    }));
  };

  // Rendu du composant de l'étape actuelle
  const CurrentStepComponent = steps[currentStep].component;

  // Affichage de chargement
  if (loading) {
    return (
      <div className={styles.settings}>
        <div className={styles.wrapper}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Chargement des informations de l'étudiant...</p>
          </div>
        </div>
      </div>
    );
  }

  // Affichage d'erreur
  if (error) {
    return (
      <div className={styles.settings}>
        <div className={styles.wrapper}>
          <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
            <p>Erreur : {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.settings}>
      {/* Navigation par étapes */}
      <div className={styles.menu}>
        {steps.map((step, index) => (
          <button
            className={cn(styles.button, {
              [styles.active]: currentStep === index,
              [styles.completed]: currentStep > index,
            })}
            key={step.id}
          >
            <span className={styles.stepNumber}>{index + 1}</span>
            {step.title}
          </button>
        ))}
      </div>

      {/* Contenu de l'étape actuelle */}
      <div className={styles.wrapper}>
        <div className={styles.stepContent}>
          <div className={styles.componentWrapper}>
            <CurrentStepComponent
              data={{ student: studentData, ...folderData }}
              updateData={updateStepData}
              currentStep={currentStep}
            />
          </div>
        </div>

        {/* Boutons de navigation */}
        {currentStep < steps.length - 1 && (
          <div className={styles.navigationButtons}>
            <button
              className="button-stroke"
              onClick={goToPreviousStep}
              disabled={currentStep === 0}
            >
              Précédent
            </button>

            <button
              className="button"
              onClick={goToNextStep}
            >
              Suivant
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentCheckInformation;
